/* eslint-disable import/no-cycle */

import { ApolloCache, makeVar } from '@apollo/client';

import { UserSessionData, WebCarts } from '@api/gql/graphql';
import { NotificationCoreFragment } from '@api/operations/fragments/types/NotificationCoreFragment';
import { UserEventsCoreFragment } from '@api/operations/fragments/types/UserEventsCoreFragment';
import { getLocalStorageItem, getStorageItem } from '@src/utils/helperFunctions';
import { getInitialCart, getInitialWishlist } from '@store/actions/cart';
import { layoutInitialState } from '@store/actions/layout';
import { navbarQueryState } from '@store/actions/navbar';
import { getStoredUserCoordinates } from '@store/actions/session';
import { getInitialAlerts, userAlertsCache } from '@store/actions/tasks';
import {
  GetAllTasksType,
  UserAlertsVar,
  UserCartProps,
  UserMessageType,
  UserWishlistProps
} from '@store/types';

// Hydrate the user session from localStorage exactly once at module-load time.
// Caching the parse means `initialUser()` and `isLoggedInVar` agree on the same
// object reference, and a corrupt blob doesn't crash boot (getLocalStorageItem
// catches and clears it for us).
const bootUser = getLocalStorageItem<UserSessionData>('userData') ?? undefined;

export const initialUser = (): UserSessionData | undefined => bootUser;

export const userSessionVar = makeVar<UserSessionData | undefined>(bootUser);
export const hasChangedPermissionVar = makeVar<boolean>(false);
export const isLoggedInVar = makeVar<boolean>(bootUser !== undefined);
export const userCartVar = makeVar<UserCartProps[]>(getInitialCart());

// TODO: migrate userCartVar usages
export const webCartVar = makeVar<WebCarts>(getInitialCart());
export const userWishlistVar = makeVar<UserWishlistProps[]>(getInitialWishlist());
export const userEventsVar = makeVar<UserEventsCoreFragment[]>(getStorageItem('userEvents') ?? []);
export const userTasksVar = makeVar<GetAllTasksType['getAllTasks']>([]);
export const userAlertsVar = makeVar<Partial<UserAlertsVar>>(getInitialAlerts())
  .attachCache(userAlertsCache as unknown as ApolloCache<any>);
// eslint-disable-next-line max-len
export const userCoordinatesVar = makeVar<Partial<GeolocationCoordinates> | undefined>(getStoredUserCoordinates());
export const userNotificationsVar = makeVar<NotificationCoreFragment[]>([]);
export const userMessagesVar = makeVar<UserMessageType[]>([]);

/* ═════════════════════════════════════════
 ** =========== LAYOUT ===========**
 ═════════════════════════════════════════ */
// Layout state is owned by `@store/actions/layout` (where the storage hydration
// lives). Re-export the initial state here so existing imports keep working.
export { layoutInitialState };

export const navbarStateVar = makeVar<any>(navbarQueryState);
export const layoutStateVar = makeVar<typeof layoutInitialState>(layoutInitialState);
export const layoutSkinStateVar = makeVar<any>(layoutInitialState.skin);
export const layoutSidebarMenuVar = makeVar<any>(layoutInitialState.menuCollapsed);
export const layoutSidebarMenuHoverVar = makeVar<boolean>(layoutInitialState.menuHover);
